import {Form, Formik} from 'formik'
import React, {Dispatch, SetStateAction, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import TextInput from '../../widgets/components/Input/TextInput'
import * as Yup from 'yup'
import {CustomButton} from '../../widgets/components/UI/CustomButton'
import {addInvestment, getSingleCompanyInvestment, updateInvestment} from '../core/_requests'
import {useAuth} from '../../auth'
import {toast} from 'react-toastify'
import {Spinner} from '../../widgets/components/General/Spinner'
import TagManager from 'react-gtm-module'
import {GTMEvent} from '../../../hooks/gtmHelpers'
import { removeNullFields } from '../../../core/_util'

export const AddInvestmentModal = ({
  showInvestmentModal,
  setShowInvestmentModal,
  companyInvestmentsId,
  updateModalStatus,
  getAllCompanyInvestment,
}: {
  showInvestmentModal: boolean
  setShowInvestmentModal: Dispatch<SetStateAction<boolean>>
  companyInvestmentsId: number | undefined
  updateModalStatus: boolean
  getAllCompanyInvestment: any
}) => {
  const {formatMessage} = useIntl()
  const [loading, setLoading] = useState(false)
  const {companyId, currentUser} = useAuth()
  const [singleInvestmentData, setSingleInvestmentData] = useState<any>()
  const [singleInvestmentDataLoading, setSingleInvestmentDataLoading] = useState(false)

  const initialValues = {
    announced_date: '',
    transaction_name: '',
    investor_count: '',
    money_raised: '',
    lead_investors: '',
  }

  const investmentSchema = Yup.object().shape({
    announced_date: Yup.string()
      .required(formatMessage({id: 'Announced Date is required'}))
      .nullable(),
    transaction_name: Yup.string()
      .min(1, formatMessage({id: 'Minimum 1 character is required'}))
      .max(50, formatMessage({id: 'Maximum 50 characters'}))
      .required(formatMessage({id: 'Transaction name is required'}))
      .nullable(),
    investor_count: Yup.number()
      .required(formatMessage({id: 'Investor Count is required'}))
      .nullable(),
    money_raised: Yup.number()
      .required(formatMessage({id: 'Money Raised is required'}))
      .nullable(),
    lead_investors: Yup.string().required(formatMessage({id: 'Lead Investors required'})),
  })

  const onSubmit = async (values: any) => {
    try {
      setLoading(true)
      if (updateModalStatus && companyInvestmentsId) {
        const {
          data: {success, errors},
        } = await updateInvestment(values, companyInvestmentsId)
        if (success) {
          getAllCompanyInvestment()
          getSingleInvestment(companyInvestmentsId)
          setLoading(false)
          setShowInvestmentModal(false)
          toast.success(formatMessage({id: 'Company Investment updated successfully'}))
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.UpdateInvestmentSuccessful,
              action: 'normal',
              metadata: removeNullFields({
                user_id: currentUser?.userId,
                email: currentUser?.email,
                announced_date: values?.announced_date || '',
                transaction_name: values?.transaction_name || '',
                investor_count: values?.investor_count || '',
                money_raised: values?.money_raised || '',
                lead_investors: values?.lead_investors || '',
                company_investment_id: companyInvestmentsId,
              }),
            },
          })
        } else {
          setLoading(false)
          errors.forEach((error: string) => {
            toast.error(formatMessage({id: error}))
          })
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.UpdateInvestmentFailed,
              action: 'normal',
              metadata: removeNullFields({
                user_id: currentUser?.userId,
                email: currentUser?.email,
                announced_date: values?.announced_date || '',
                transaction_name: values?.transaction_name || '',
                investor_count: values?.investor_count || '',
                money_raised: values?.money_raised || '',
                lead_investors: values?.lead_investors || '',
                company_investment_id: companyInvestmentsId,
                error: errors[0],
              }),
            },
          })
        }
      } else {
        if (companyId) {
          const {
            data: {success, errors},
          } = await addInvestment(values, companyId)
          if (success) {
            getAllCompanyInvestment()
            setLoading(false)
            setShowInvestmentModal(false)
            toast.success(formatMessage({id: 'Company Investment Added successfully'}))
            TagManager.dataLayer({
              dataLayer: {
                event: GTMEvent.AddInvestmentSuccessful,
                action: 'normal',
                metadata: removeNullFields({
                  user_id: currentUser?.userId,
                  email: currentUser?.email,
                  announced_date: values?.announced_date || '',
                  transaction_name: values?.transaction_name || '',
                  investor_count: values?.investor_count || '',
                  money_raised: values?.money_raised || '',
                  lead_investors: values?.lead_investors || '',
                }),
              },
            })
          } else {
            setLoading(false)
            errors.forEach((error: string) => {
              toast.error(formatMessage({id: error}))
            })
            TagManager.dataLayer({
              dataLayer: {
                event: GTMEvent.AddInvestmentFailed,
                action: 'normal',
                metadata: removeNullFields({
                  user_id: currentUser?.userId,
                  email: currentUser?.email,
                  announced_date: values?.announced_date || '',
                  transaction_name: values?.transaction_name || '',
                  investor_count: values?.investor_count || '',
                  money_raised: values?.money_raised || '',
                  lead_investors: values?.lead_investors || '',
                  error: errors[0],
                }),
              },
            })
          }
        }
      }
    } catch (err) {
      setLoading(false)
      console.log(err)
    }
  }

  const getSingleInvestment = async (companyInvestmentsId: number) => {
    try {
      setSingleInvestmentDataLoading(true)
      const {
        data: {data, success, errors},
      } = await getSingleCompanyInvestment(companyInvestmentsId)
      if (success) {
        setSingleInvestmentData(data)
        setSingleInvestmentDataLoading(false)
      } else {
        setSingleInvestmentDataLoading(false)
        errors.forEach((error: string) => {
          toast.error(formatMessage({id: error}))
        })
      }
    } catch (err) {
      setSingleInvestmentDataLoading(false)
      console.log(err)
    }
  }

  useEffect(() => {
    if (companyInvestmentsId) {
      getSingleInvestment(companyInvestmentsId)
    }
  }, [companyInvestmentsId]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal
      size='lg'
      show={showInvestmentModal}
      onHide={() => setShowInvestmentModal(false)}
      aria-labelledby='contained-modal-title-vcenter'
      centered
    >
      {singleInvestmentDataLoading ? <Spinner /> : null}
      <Formik initialValues={initialValues} validationSchema={investmentSchema} onSubmit={onSubmit}>
        {(formik) => {
          if (updateModalStatus && singleInvestmentData) {
            formik.initialValues.announced_date = singleInvestmentData?.announced_date.split('T')[0]
            formik.initialValues.transaction_name = singleInvestmentData?.transaction_name
            formik.initialValues.investor_count = singleInvestmentData?.investor_count
            formik.initialValues.money_raised = singleInvestmentData?.money_raised
            formik.initialValues.lead_investors = singleInvestmentData?.lead_investors
          } else {
            formik.initialValues.announced_date = ''
            formik.initialValues.transaction_name = ''
            formik.initialValues.investor_count = ''
            formik.initialValues.money_raised = ''
            formik.initialValues.lead_investors = ''
          }
          return (
            <Form>
              <Modal.Body>
                <div className='d-flex justify-content-between m-5'>
                  <h2>
                    {updateModalStatus
                      ? formatMessage({id: 'Edit Investment'})
                      : formatMessage({id: 'Add Investment'})}
                  </h2>
                  <img
                    src={toAbsoluteUrl('/media/icons/investor/Cancel.svg')}
                    alt={formatMessage({id: 'cancel'})}
                    className='cursor-pointer'
                    onClick={() => {
                      setShowInvestmentModal(false)
                    }}
                  />
                </div>
                <div className='mt-8 m-5'>
                  <div className='row d-md-flex justify-content-between w-100'>
                    <div className='col-md-6'>
                      <TextInput
                        fieldType={'date'}
                        label={formatMessage({id: 'Announced Date'})}
                        fieldName={'announced_date'}
                        formik={formik}
                        placeholder={formatMessage({id: 'Select Date'})}
                        toolTipText={formatMessage({
                          id: 'GLOBAL.TOOLTIP.ADD_INVESTMENT_MODAL.ANNOUNCED_DATE',
                        })}
                        isStarRequired={true}
                      />
                    </div>
                    <div className='col-md-6'>
                      <TextInput
                        fieldType={'text'}
                        label={formatMessage({id: 'Transaction Name'})}
                        fieldName={'transaction_name'}
                        formik={formik}
                        placeholder={formatMessage({id: 'Enter Transaction Name'})}
                        toolTipText={formatMessage({
                          id: 'GLOBAL.TOOLTIP.ADD_INVESTMENT_MODAL.TRANSACTION_NAME',
                        })}
                        isStarRequired={true}
                      />
                    </div>
                  </div>
                  <div className='row d-md-flex justify-content-between w-100'>
                    <div className='col-md-6'>
                      <TextInput
                        fieldType={'number'}
                        fieldName={'investor_count'}
                        formik={formik}
                        placeholder={formatMessage({id: 'Enter Investor Count'})}
                        label={formatMessage({id: 'Investor Count'})}
                        toolTipText={formatMessage({
                          id: 'GLOBAL.TOOLTIP.ADD_INVESTMENT_MODAL.INVESTOR_COUNT',
                        })}
                        isStarRequired={true}
                      />
                    </div>
                    <div className='col-md-6'>
                      <TextInput
                        fieldType={'number'}
                        fieldName={'money_raised'}
                        formik={formik}
                        placeholder={formatMessage({id: 'Enter Money Raised'})}
                        label={formatMessage({id: 'Money Raised(USD)'})}
                        toolTipText={formatMessage({
                          id: 'GLOBAL.TOOLTIP.ADD_INVESTMENT_MODAL.MONEY_RAISED',
                        })}
                        isStarRequired={true}
                      />
                    </div>
                  </div>
                  <div className='row d-md-flex justify-content-between w-100'>
                    <div className='col-md-6'>
                      <TextInput
                        fieldType={'text'}
                        label={formatMessage({id: 'Lead Investors'})}
                        fieldName={'lead_investors'}
                        formik={formik}
                        placeholder={formatMessage({id: 'Enter Lead Investors'})}
                        toolTipText={formatMessage({
                          id: 'GLOBAL.TOOLTIP.ADD_INVESTMENT_MODAL.LEAD_INVESTORS',
                        })}
                        isStarRequired={true}
                      />
                    </div>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer className='p-0'>
                <button
                  type='button'
                  className='btn btn-light font-size-13 font-weight-400'
                  onClick={() => {
                    setShowInvestmentModal(false)
                  }}
                >
                  {formatMessage({id: 'Cancel'})}
                </button>
                <CustomButton
                  isSubmitting={formik.isSubmitting}
                  isValid={formik.isValid}
                  buttonText={formatMessage({
                    id: updateModalStatus ? 'Edit Investment' : 'Add Investment',
                  })}
                  loading={loading}
                  widthLoading={2}
                  width={2}
                  margin={'me-5 mt-5'}
                />
              </Modal.Footer>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}
